import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";


// Extract the 5 menopause-core hormone markers from a base64-encoded
// PDF or image of a lab report using Lovable AI (Gemini 2.5 Pro).
//
// Returns numbers (or null if not found) for: estradiol, fsh, lh,
// progesterone, tsh — plus an optional ISO labDate and labName.

const inputSchema = z.object({
  accessToken: z.string().min(10),
  fileBase64: z.string().min(1).max(15_000_000), // ~11 MB raw file
  mimeType: z.string().min(1).max(100),
});

const extractedSchema = z.object({
  estradiol_value: z.number().nullable().optional(),
  estradiol_unit: z.string().nullable().optional(),
  fsh_value: z.number().nullable().optional(),
  fsh_unit: z.string().nullable().optional(),
  lh_value: z.number().nullable().optional(),
  lh_unit: z.string().nullable().optional(),
  progesterone_value: z.number().nullable().optional(),
  progesterone_unit: z.string().nullable().optional(),
  tsh_value: z.number().nullable().optional(),
  tsh_unit: z.string().nullable().optional(),
  lab_date: z.string().nullable().optional(), // ISO YYYY-MM-DD
  lab_name: z.string().nullable().optional(),
});

export type ExtractedLab = z.infer<typeof extractedSchema>;

export const extractLabValues = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => inputSchema.parse(data))
  .handler(async ({ data }): Promise<ExtractedLab> => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    await assertActiveAccess(userId);
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");

    const systemPrompt =
      "You are a medical lab report parser. You extract the 5 menopause-core hormone markers (Estradiol, FSH, LH, Progesterone, TSH) from a single lab report. Return null for any value you cannot find. Never guess. Numbers only — no units inside the number field.";

    // Use a tool/function call so we get strict structured output.
    const tools = [
      {
        type: "function",
        function: {
          name: "save_lab_values",
          description: "Save the extracted lab values from this report.",
          parameters: {
            type: "object",
            properties: {
              estradiol_value: { type: ["number", "null"], description: "Numeric value only, no units" },
              estradiol_unit: { type: ["string", "null"], description: "e.g. pg/mL or pmol/L" },
              fsh_value: { type: ["number", "null"] },
              fsh_unit: { type: ["string", "null"], description: "e.g. mIU/mL or IU/L" },
              lh_value: { type: ["number", "null"] },
              lh_unit: { type: ["string", "null"], description: "e.g. mIU/mL or IU/L" },
              progesterone_value: { type: ["number", "null"] },
              progesterone_unit: { type: ["string", "null"], description: "e.g. ng/mL or nmol/L" },
              tsh_value: { type: ["number", "null"] },
              tsh_unit: { type: ["string", "null"], description: "e.g. µIU/mL or mIU/L" },
              lab_date: {
                type: ["string", "null"],
                description: "Date the blood was drawn or the report was issued, in ISO format YYYY-MM-DD. Null if not found.",
              },
              lab_name: {
                type: ["string", "null"],
                description: "Name of the lab or clinic that issued the report (e.g. 'Quest Diagnostics'). Null if not found.",
              },
            },
            required: [
              "estradiol_value", "estradiol_unit",
              "fsh_value", "fsh_unit",
              "lh_value", "lh_unit",
              "progesterone_value", "progesterone_unit",
              "tsh_value", "tsh_unit",
              "lab_date", "lab_name",
            ],
            additionalProperties: false,
          },
        },
      },
    ];

    const dataUrl = `data:${data.mimeType};base64,${data.fileBase64}`;

    const body = {
      model: "google/gemini-2.5-pro",
      messages: [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content: [
            { type: "text", text: "Extract the 5 menopause-core hormone values from this lab report." },
            { type: "image_url", image_url: { url: dataUrl } },
          ],
        },
      ],
      tools,
      tool_choice: { type: "function", function: { name: "save_lab_values" } },
    };

    const resp = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      if (resp.status === 429) {
        throw new Error("Lab extraction is busy right now — please wait a minute and try again.");
      }
      if (resp.status === 402) {
        throw new Error("Lab extraction credits are exhausted. Please contact support.");
      }
      const t = await resp.text().catch(() => "");
      console.error("[lab-extract] gateway error", resp.status, t);
      throw new Error(`Lab extraction failed (${resp.status})`);
    }

    const json: any = await resp.json();
    const call = json?.choices?.[0]?.message?.tool_calls?.[0];
    const argsStr = call?.function?.arguments;
    if (!argsStr) {
      throw new Error("We couldn't read this file. Try a clearer photo or a PDF of just the lab report page.");
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(argsStr);
    } catch {
      throw new Error("Lab extraction returned an invalid response.");
    }

    return extractedSchema.parse(parsed);
  });
